import type { ClientListItem } from "@planerius/contracts/cabinet"

import { dateDiffInDays } from "../../journal/journal-time"
import { formatDateLong, todayInZone } from "../../lib/format"
import { visitsLabel } from "./master-text"

/**
 * «Мои клиенты» (5-М4) как данные.
 *
 * ГРАНИЦА ГРУПП И ПОЧЕМУ ОНА ИМЕННО ТАКАЯ. Макет делит список на «Были в этом месяце»
 * и «Давно не были». Граница взята буквально по заголовку — 31 день: заголовок это
 * обещание, и клиент, приходивший 12 августа, не может стоять под словами «в этом месяце»
 * шестнадцатого сентября. В самом прототипе он там стоит — расхождение вынесено
 * в отчёт: либо у группы другое название, либо пример в макете собран наспех.
 *
 * ЧТО ЭТОТ СПИСОК НЕ ЕСТЬ. Это не база клиентов салона: сервер сужает выдачу до тех,
 * у кого есть хоть одна запись к этому мастеру (признак роли restrict_to_own_records,
 * clients.service.ts). Мастер не должен получить доступ к чужим клиентам ни через этот
 * экран, ни через форму записи — это прямое требование ТЗ §5.2, а не пожелание.
 */

/** Сколько дней «в этом месяце». Ровно столько, чтобы заголовок группы не врал. */
export const RECENT_DAYS = 31

export interface MasterClientRow {
  readonly id: string
  readonly displayName: string
  readonly initials: string
  /** «12 визитов · сегодня» — подпись под именем. */
  readonly subtitle: string
  readonly hasNoShow: boolean
  /** Полный номер. null — телефон мастеру не открыт: кнопки звонка нет вовсе. */
  readonly phone: string | null
}

export interface MasterClientGroup {
  readonly title: string
  readonly items: readonly MasterClientRow[]
}

function initialsOf(displayName: string): string {
  return displayName
    .trim()
    .split(/\s+/u)
    .slice(0, 2)
    .map((word) => word.slice(0, 1).toUpperCase())
    .join("")
}

/** «сегодня», «вчера», «12 августа». Никогда не «01.09.2026»: так дату не проговаривают. */
export function lastVisitLabel(
  lastVisitAt: string | null,
  timeZone: string,
  today: string,
): string {
  if (lastVisitAt === null) return "визитов ещё не было"
  const day = todayInZone(timeZone, new Date(lastVisitAt))
  const diff = dateDiffInDays(day, today)
  if (diff === 0) return "сегодня"
  if (diff === 1) return "вчера"
  // Название дня недели в этой строке лишнее: важен день, а не «ср».
  return formatDateLong(day, timeZone).replace(/^[^,]+,\s*/u, "")
}

export function buildClientGroups(input: {
  readonly items: readonly ClientListItem[]
  readonly timeZone: string
  readonly today: string
}): readonly MasterClientGroup[] {
  const recent: MasterClientRow[] = []
  const old: MasterClientRow[] = []

  for (const item of input.items) {
    const row: MasterClientRow = {
      id: item.id,
      displayName: item.displayName,
      initials: initialsOf(item.displayName),
      subtitle: `${visitsLabel(item.visitsCount)} · ${lastVisitLabel(item.lastVisitAt, input.timeZone, input.today)}`,
      hasNoShow: item.noShowCount > 0,
      // Телефон гасит сервер: здесь он либо есть, либо его нет, и второй раз
      // это решение не пересчитывается.
      phone: item.phone,
    }

    const days =
      item.lastVisitAt === null
        ? Number.POSITIVE_INFINITY
        : dateDiffInDays(todayInZone(input.timeZone, new Date(item.lastVisitAt)), input.today)

    if (days <= RECENT_DAYS) recent.push(row)
    else old.push(row)
  }

  const groups: MasterClientGroup[] = []
  if (recent.length > 0) groups.push({ title: "Были в этом месяце", items: recent })
  if (old.length > 0) groups.push({ title: "Давно не были", items: old })
  return groups
}
